import { paths } from '@common/types';
import {
  axiosExternalInstance,
  axiosInstance,
} from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

interface IUseSaveAssessmentMutationProps {
  patient_id: string;
  assessment_id: string;
}

const saveAssessmentRequest = async ({
  patient_id,
  ...props
}: paths['/patient/{patient_id}/assessment/{assessment_id}']['put']['requestBody']['content']['application/json'] & {
  patient_id: string;
  assessment_id: string;
}) => {
  const response = axiosInstance.put<
    paths['/patient/{patient_id}/assessment/{assessment_id}']['put']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/patient/{patient_id}/assessment/{assessment_id}']['put']['responses']['200']['content']['application/json']
    >
  >(`/patient/${patient_id}/assessment/${props.assessment_id}`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useSaveAssessmentMutation = ({
  patient_id,
  assessment_id,
}: IUseSaveAssessmentMutationProps) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['saveAssessment'],
    mutationFn: (
      props: paths['/patient/{patient_id}/assessment/{assessment_id}']['put']['requestBody']['content']['application/json'],
    ) => saveAssessmentRequest({ ...props, patient_id, assessment_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['assessments'] }),
        client.invalidateQueries({
          queryKey: ['assessment', patient_id, assessment_id],
        }),
        client.invalidateQueries({
          queryKey: ['programStatusHistory', patient_id],
        }),
        client.invalidateQueries({
          queryKey: ['task'],
        }),
      ]),
  });

  return mutation;
};

interface IUseSaveExternalAssessmentMutationProps {
  token: string;
  assessment_id: string;
}

const saveExternalAssessmentRequest = async ({
  token,
  assessment_id,
  form_data,
}: paths['/assessment/{assessment_id}']['patch']['requestBody']['content']['application/json'] &
  IUseSaveExternalAssessmentMutationProps) => {
  const response = axiosExternalInstance.patch<
    paths['/assessment/{assessment_id}']['patch']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/patient/{patient_id}/assessment/{assessment_id}']['put']['responses']['200']['content']['application/json']
    >
  >(`/assessment/${assessment_id}?token=${token}`, {
    form_data,
    token,
  });
  const data = await response;

  return data;
};

export const useSaveExternalAssessmentMutation = ({
  token,
  assessment_id,
}: IUseSaveExternalAssessmentMutationProps) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['saveExternalAssessment'],
    mutationFn: (
      props: paths['/assessment/{assessment_id}']['patch']['requestBody']['content']['application/json'],
    ) => saveExternalAssessmentRequest({ ...props, token, assessment_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['externalAssessment', assessment_id, token],
        }),
      ]),
  });

  return mutation;
};

interface ICreateAssessmentExternalLinkProps {
  patient_id: string;
  assessment_id: string;
}

const createAssessmentExternalLinkRequest = async ({
  assessment_id,
  patient_id,
}: ICreateAssessmentExternalLinkProps) => {
  const response = await axiosInstance.post<
    paths['/patient/{patient_id}/assessment/{assessment_id}/generate-link']['post']['requestBody'],
    AxiosResponse<
      paths['/patient/{patient_id}/assessment/{assessment_id}/generate-link']['post']['responses']['201']['content']['application/json']
    >
  >(`/patient/${patient_id}/assessment/${assessment_id}/generate-link`);
  const data = response;

  return data;
};

//This will only create a shareable link for an existing assessment
export const useCreateAssessmentExternalLinkMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['createAssessmentExternalLink'],
    mutationFn: (props: ICreateAssessmentExternalLinkProps) =>
      createAssessmentExternalLinkRequest(props),
    onSuccess: () =>
      Promise.all([client.invalidateQueries({ queryKey: ['assessment'] })]),
  });

  return mutation;
};

interface ICreateAssessmentAndLinkProps {
  patient_id: string;
}

const createAssessmentAndLinkRequest = async ({
  patient_id,
  ...props
}: paths['/patient/{patient_id}/assessment-link']['post']['requestBody']['content']['application/json'] &
  ICreateAssessmentAndLinkProps) => {
  const response = await axiosInstance.post<
    paths['/patient/{patient_id}/assessment-link']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/patient/{patient_id}/assessment-link']['post']['responses']['201']['content']['application/json']
    >
  >(`/patient/${patient_id}/assessment-link`, props);
  const data = response;

  return data;
};

//This will create both the assessment and it's shareable link
export const useCreateAssessmentAndLinkMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['createAssessmentAndLink'],
    mutationFn: (
      props: paths['/patient/{patient_id}/assessment-link']['post']['requestBody']['content']['application/json'],
    ) => createAssessmentAndLinkRequest({ patient_id, ...props }),
    onSuccess: () =>
      Promise.all([client.invalidateQueries({ queryKey: ['assessments'] })]),
  });

  return mutation;
};

const createAssessmentRequest = async (
  patient_id: string,
  props: paths['/patient/{patient_id}/assessment']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/patient/{patient_id}/assessment']['post']['responses']['201']['content'],
    AxiosResponse<
      paths['/patient/{patient_id}/assessment']['post']['responses']['201']['content']['application/json']
    >
  >(`/patient/${patient_id}/assessment`, props);
  const data = await response;

  return data;
};

export const useCreateAssessmentMutation = (patient_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['createAssessment'],
    mutationFn: (
      props: paths['/patient/{patient_id}/assessment']['post']['requestBody']['content']['application/json'],
    ) => createAssessmentRequest(patient_id, props),
    onSuccess: _ =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['assessments'] }),
        client.invalidateQueries({
          queryKey: ['patient', patient_id],
        }),
        client.invalidateQueries({ queryKey: ['tasks'] }),
      ]),
  });

  return mutation;
};

interface IDownloadAssessmentPdfParams {
  patient_id: string;
  assessment_id: string;
}

const downloadAssessmentPdfRequest = async ({
  patient_id,
  assessment_id,
}: IDownloadAssessmentPdfParams) => {
  const response = await axiosInstance.get<Blob>(
    `/patient/${patient_id}/assessment/${assessment_id}/pdf`,
    {
      responseType: 'blob',
    },
  );
  const result = response;

  return result;
};

export const useDownloadAssessmentPdfMutation = () => {
  const mutation = useMutation({
    mutationKey: ['downloadAssessmentPdf'],
    mutationFn: (props: IDownloadAssessmentPdfParams) =>
      downloadAssessmentPdfRequest(props),
  });

  return mutation;
};

interface IDeleteAssessmentParams {
  patient_id: string;
  assessment_id: string;
}

const deleteAssessmentRequest = async ({
  patient_id,
  assessment_id,
}: IDeleteAssessmentParams) => {
  const response = await axiosInstance.delete(
    `/patient/${patient_id}/assessment/${assessment_id}`,
  );

  return response;
};

export const useDeleteAssessmentMutation = ({
  patient_id,
}: {
  patient_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deleteAssessment'],
    mutationFn: ({ assessment_id }: { assessment_id: string }) =>
      deleteAssessmentRequest({ patient_id, assessment_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['assessments'] }),
        client.invalidateQueries({
          queryKey: ['patient', patient_id],
        }),
        client.invalidateQueries({
          queryKey: ['programStatusHistory', patient_id],
        }),
        client.invalidateQueries({ queryKey: ['tasks'] }),
        client.invalidateQueries({ queryKey: ['task'] }),
      ]),
  });

  return mutation;
};
